import React, { useEffect, useMemo, useState } from 'react';
import { apiGet, apiPost, apiPut, apiDelete } from '../../api/client';
import AdminModal from '../../components/admin/AdminModal';
import { UPDATE_TYPE_LABELS, formatUpdateDate } from '../../components/UpdatesTimeline';

const today = () => new Date().toISOString().slice(0, 10);

const emptyUpdate = () => ({
  productId: '',
  title: '',
  version: '',
  type: 'update',
  summary: '',
  link: '',
  date: today(),
  published: true,
});

const TYPE_PILLS = { new: 'is-green', update: 'is-purple', fix: 'is-muted' };

export default function AdminUpdates() {
  const [updates, setUpdates] = useState([]);
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(emptyUpdate);
  const [editingId, setEditingId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [formError, setFormError] = useState('');
  const [search, setSearch] = useState('');

  const load = () => apiGet('/updates?all=1').then(setUpdates);

  useEffect(() => {
    load().catch(console.error);
    apiGet('/products').then(setProducts).catch(console.error);
  }, []);

  const productOptions = useMemo(
    () => [...products].sort((a, b) => a.name.localeCompare(b.name)),
    [products],
  );

  const query = search.trim().toLowerCase();
  const visible = updates.filter((u) => !query
    || [u.title, u.summary, u.version, u.product?.name].join(' ').toLowerCase().includes(query));

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
    setForm(emptyUpdate());
    setFormError('');
  };

  const startAdd = (productId = '') => {
    setEditingId(null);
    setForm({ ...emptyUpdate(), productId });
    setFormError('');
    setModalOpen(true);
  };

  const startEdit = (u) => {
    setEditingId(u.id);
    setForm({
      productId: u.productId || '',
      title: u.title,
      version: u.version,
      type: u.type,
      summary: u.summary,
      link: u.link,
      date: u.date,
      published: u.published,
    });
    setFormError('');
    setModalOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setFormError('');
    try {
      if (editingId) {
        await apiPut(`/updates/${editingId}`, form);
        setMessage(`Updated “${form.title}”.`);
      } else {
        await apiPost('/updates', form);
        setMessage(`Posted “${form.title}”.`);
      }
      closeModal();
      load();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Delete the update “${u.title}”?`)) return;
    await apiDelete(`/updates/${u.id}`);
    setMessage(`Deleted “${u.title}”.`);
    load();
  };

  const togglePublished = async (u) => {
    await apiPut(`/updates/${u.id}`, { ...u, published: !u.published });
    load();
  };

  const selectedProduct = products.find((p) => p.id === form.productId);

  return (
    <div className="admin-panel-page">
      <div className="admin-panel-toolbar">
        <div className="admin-panel-toolbar-row">
          <div className="admin-stat-chips">
            <div className="admin-stat-chip">
              <span className="admin-stat-value">{updates.length}</span>
              <span className="admin-stat-label">Total</span>
            </div>
            <div className="admin-stat-chip admin-stat-chip--green">
              <span className="admin-stat-value">{updates.filter((u) => u.published).length}</span>
              <span className="admin-stat-label">Published</span>
            </div>
            <div className="admin-stat-chip admin-stat-chip--purple">
              <span className="admin-stat-value">{updates.filter((u) => !u.published).length}</span>
              <span className="admin-stat-label">Drafts</span>
            </div>
          </div>
          <div className="admin-panel-toolbar-actions">
            <a href="/updates" target="_blank" rel="noopener noreferrer" className="btn btn-secondary">
              <i className="fas fa-external-link-alt" aria-hidden="true" /> View page
            </a>
            <button type="button" className="btn btn-primary" onClick={() => startAdd()}>
              <i className="fas fa-plus" aria-hidden="true" /> Post update
            </button>
          </div>
        </div>
        <div className="admin-filter-row">
          <div className="admin-search">
            <i className="fas fa-search" aria-hidden="true" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search updates, tools, versions…" />
          </div>
          <span className="admin-filter-hint">Newest first · the home page shows the latest few, /updates shows all</span>
        </div>
      </div>

      {message && <p className="admin-message">{message}</p>}

      <div className="admin-list-wrap">
        {visible.length === 0 ? (
          <div className="admin-list-empty">
            <p>{updates.length ? 'No updates match this search.' : 'No updates posted yet.'}</p>
            {!updates.length && <button type="button" className="btn btn-primary" onClick={() => startAdd()}>Post the first update</button>}
          </div>
        ) : (
          <ul className="admin-list">
            {visible.map((u) => (
              <li className={`admin-list-item${u.published ? '' : ' is-draft'}`} key={u.id}>
                {u.product?.image ? (
                  <img src={u.product.image} alt="" className="admin-thumb admin-thumb--wide" />
                ) : (
                  <span className="admin-thumb admin-thumb--wide is-empty"><i className="fas fa-code-branch" aria-hidden="true" /></span>
                )}
                <div className="admin-list-main">
                  <div className="admin-list-title-row">
                    <span className="admin-list-title">{u.title}</span>
                  </div>
                  {u.summary && <p className="admin-list-desc">{u.summary}</p>}
                  <div className="admin-list-pills">
                    <span className={`admin-pill ${TYPE_PILLS[u.type] || ''}`}>{UPDATE_TYPE_LABELS[u.type] || u.type}</span>
                    {u.product?.name && <span className="admin-pill">{u.product.name}</span>}
                    {u.version && <span className="admin-pill is-muted">v{u.version.replace(/^v/i, '')}</span>}
                    <span className="admin-pill is-muted">{formatUpdateDate(u.date)}</span>
                    {!u.published && <span className="admin-pill is-muted"><i className="fas fa-eye-slash" aria-hidden="true" /> Draft</span>}
                  </div>
                </div>
                <div className="admin-list-actions">
                  <button
                    type="button"
                    className={`admin-icon-btn ${u.published ? 'is-active' : ''}`}
                    onClick={() => togglePublished(u)}
                    title={u.published ? 'Unpublish (keep as draft)' : 'Publish'}
                  >
                    <i className={`fas ${u.published ? 'fa-eye' : 'fa-eye-slash'}`} aria-hidden="true" />
                  </button>
                  <button type="button" className="admin-icon-btn" onClick={() => startAdd(u.productId || '')} title="Post another update for this tool">
                    <i className="fas fa-plus" aria-hidden="true" />
                  </button>
                  <button type="button" className="admin-icon-btn" onClick={() => startEdit(u)} title="Edit">
                    <i className="fas fa-pen" aria-hidden="true" />
                  </button>
                  <button type="button" className="admin-icon-btn danger" onClick={() => handleDelete(u)} title="Delete">
                    <i className="fas fa-trash" aria-hidden="true" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <AdminModal
        open={modalOpen}
        as="form"
        onSubmit={handleSave}
        onClose={closeModal}
        size="lg"
        title={editingId ? 'Edit update' : 'Post update'}
        subtitle={editingId ? null : 'Shows on the home page changelog and on /updates.'}
        footer={(
          <>
            {formError && <span className="admin-modal__error">{formError}</span>}
            <button type="button" className="btn btn-secondary" onClick={closeModal}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : editingId ? 'Save changes' : 'Post update'}
            </button>
          </>
        )}
      >
        <div className="admin-modal-split">
          <div className="admin-modal-fields">
            <div className="admin-field-row">
              <label className="admin-field">
                <span>Tool</span>
                <select value={form.productId} onChange={(e) => setForm({ ...form, productId: e.target.value })}>
                  <option value="">— Not tied to a tool —</option>
                  {productOptions.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </label>
              <label className="admin-field">
                <span>Type</span>
                <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                  {Object.entries(UPDATE_TYPE_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </label>
            </div>
            <label className="admin-field">
              <span>Title</span>
              <input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Switch 2 theme and song replays"
                required
                autoFocus
              />
            </label>
            <label className="admin-field">
              <span>What changed</span>
              <textarea rows={4} value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} placeholder="One or two sentences for the changelog." />
            </label>
            <div className="admin-field-row">
              <label className="admin-field">
                <span>Version</span>
                <input value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} placeholder="1.2.0 (optional)" />
              </label>
              <label className="admin-field">
                <span>Date</span>
                <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required />
              </label>
            </div>
            <label className="admin-field">
              <span>Link</span>
              <input
                value={form.link}
                onChange={(e) => setForm({ ...form, link: e.target.value })}
                placeholder={selectedProduct?.buttonLink ? `Defaults to ${selectedProduct.buttonLink}` : '/tools/… or https://…'}
              />
            </label>
            <label className="admin-switch">
              <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} />
              <span>Published (untick to save as a draft)</span>
            </label>
          </div>
          <aside className="admin-modal-side">
            <div className="admin-card-preview">
              {selectedProduct?.overlayImage
                ? <img src={selectedProduct.overlayImage} alt="" />
                : <div className="admin-card-preview__empty"><i className="fas fa-code-branch" aria-hidden="true" /></div>}
              <div className="admin-card-preview__body">
                <span className={`admin-pill ${TYPE_PILLS[form.type] || ''}`}>{UPDATE_TYPE_LABELS[form.type]}</span>
                <strong>{form.title || 'Update title'}</strong>
                <p>{form.summary || 'What changed shows here.'}</p>
                <span className="admin-card-preview__btn">{formatUpdateDate(form.date)}{form.version ? ` · v${form.version.replace(/^v/i, '')}` : ''}</span>
              </div>
            </div>
          </aside>
        </div>
      </AdminModal>
    </div>
  );
}
